import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { createKey, deleteKey, fetchKeyById, fetchKeys, regenerateKey, updateKey, validateApiKey } from '../api';
import { CACHE_INVALIDATION_DELAY_MS, QUERY_STALE_TIME_MS } from '../consts';

import type { UseMutationOptions, QueryClient } from '@tanstack/react-query';
import type { ListResult, Params } from '@codes/shared';
import type { CreateKeyDTO, KeyBody, KeyView, ValidatedUserProfile } from '../types';

// Query keys factory for cache management
export const codesKeyQueryKeys = {
    all: ['codes-keys'] as const,
    lists: () => [...codesKeyQueryKeys.all, 'list'] as const,
    list: (params?: Params) => [...codesKeyQueryKeys.lists(), params] as const,
    details: () => [...codesKeyQueryKeys.all, 'detail'] as const,
    detail: (id: string) => [...codesKeyQueryKeys.details(), id] as const,
};

/**
 * Update query cache with new key (optimistic update)
 */
const updateCacheWithNewKey = (queryClient: QueryClient, newKey: KeyView) => {
    const queryKey = codesKeyQueryKeys.list({ view: 'user' });

    queryClient.setQueryData<ListResult<KeyView>>(queryKey, oldData => {
        if (!oldData) return oldData;

        return {
            ...oldData,
            total: oldData.total + 1,
            list: [newKey, ...oldData.list],
        };
    });
};

/**
 * Update both list and detail caches with updated key (optimistic update)
 */
const updateCacheWithUpdatedKey = (queryClient: QueryClient, updatedKey: KeyView) => {
    // Update detail cache
    queryClient.setQueryData<KeyView>(codesKeyQueryKeys.detail(updatedKey.id), updatedKey);

    // Update list cache
    const listQueryKey = codesKeyQueryKeys.list({ view: 'user' });
    queryClient.setQueryData<ListResult<KeyView>>(listQueryKey, oldData => {
        if (!oldData) return oldData;

        return {
            ...oldData,
            list: oldData.list.map(key => (key.id === updatedKey.id ? updatedKey : key)),
        };
    });
};

/**
 * Remove deleted key from list cache (optimistic update)
 */
const removeCacheDeletedKey = (queryClient: QueryClient, deletedKeyId: string) => {
    const queryKey = codesKeyQueryKeys.list({ view: 'user' });

    queryClient.setQueryData<ListResult<KeyView>>(queryKey, oldData => {
        if (!oldData) return oldData;

        return {
            ...oldData,
            total: oldData.total - 1,
            list: oldData.list.filter(key => key.id !== deletedKeyId),
        };
    });
};

/**
 * Hook to fetch list of Codes keys
 */
export const useCodesKeyList = (config?: { params?: Params }) => {
    return useQuery({
        queryKey: codesKeyQueryKeys.list(config?.params),
        queryFn: () => fetchKeys(config?.params),
        staleTime: QUERY_STALE_TIME_MS,
    });
};

/**
 * Hook to fetch a single Codes key by ID
 */
export const useCodesKeyDetail = (keyId: string, options?: { enabled?: boolean }) => {
    return useQuery({
        queryKey: codesKeyQueryKeys.detail(keyId),
        queryFn: () => fetchKeyById(keyId),
        staleTime: QUERY_STALE_TIME_MS,
        enabled: options?.enabled ?? !!keyId, // Only fetch if keyId exists
    });
};

/**
 * Hook to create a new Codes key with optimistic UI updates
 */
export const useCreateCodesKey = (
    options?: UseMutationOptions<KeyView, Error, { body: CreateKeyDTO; mocks?: boolean }>
) => {
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        mutationFn: ({ body, mocks }) => createKey(body, mocks),
        onSuccess: async (data, variables, context) => {
            updateCacheWithNewKey(queryClient, data);
            await options?.onSuccess?.(data, variables, context);
        },
    });
};

/**
 * Hook to update an existing Codes key with optimistic UI updates
 */
export const useUpdateCodesKey = (
    options?: UseMutationOptions<KeyView, Error, { id: string; body: Partial<KeyBody> }>
) => {
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        mutationFn: async ({ id, body }) => updateKey({ keyId: id, ...body }),
        onSuccess: async (data, variables, context) => {
            updateCacheWithUpdatedKey(queryClient, data);
            await options?.onSuccess?.(data, variables, context);
        },
    });
};

/**
 * Hook to delete a Codes key with optimistic UI updates
 */
export const useDeleteCodesKey = (options?: UseMutationOptions<KeyView, Error, { id: string }>) => {
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        mutationFn: ({ id }) => deleteKey(id),
        onSuccess: async (data, variables, context) => {
            removeCacheDeletedKey(queryClient, variables.id);

            setTimeout(() => {
                queryClient.invalidateQueries({ queryKey: codesKeyQueryKeys.lists() });
            }, CACHE_INVALIDATION_DELAY_MS);

            await options?.onSuccess?.(data, variables, context);
        },
    });
};

/**
 * Hook to regenerate a Codes key with optimistic UI updates
 * This revokes the old key and creates a new one
 */
export const useRegenerateCodesKey = (options?: UseMutationOptions<KeyView, Error, { id: string }>) => {
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        mutationFn: ({ id }) => regenerateKey(id),
        onSuccess: async (data, variables, context) => {
            updateCacheWithUpdatedKey(queryClient, data);

            setTimeout(() => {
                queryClient.invalidateQueries({ queryKey: codesKeyQueryKeys.all });
            }, CACHE_INVALIDATION_DELAY_MS);

            await options?.onSuccess?.(data, variables, context);
        },
    });
};

/**
 * Hook to validate an API key and retrieve user profile
 *
 * @example
 * const validateMutation = useValidateApiKey({
 *   onSuccess: (profile) => console.log('Valid!', profile),
 *   onError: (error) => toast.error('Invalid key'),
 * });
 *
 * validateMutation.mutate({ apiKey: 'sk-...' });
 */
export const useValidateApiKey = (options?: UseMutationOptions<ValidatedUserProfile, Error, { apiKey: string }>) => {
    return useMutation({
        mutationFn: ({ apiKey }) => validateApiKey(apiKey),
        ...options,
    });
};
